import { ReactNode } from "react";

import { ExamTiming, TaskKind } from "@/generated/graphql";
import { formatFullSentence } from "@/utils/datetime";

import { AssessmentNoun } from "../../assessment-utils";
import { ActionableRequirementRow, RequirementRow } from "./shared";

/** Display the exam feedback return date. */
export function ExamReturnDate(props: {
  examReturnDate: string | null;
  hasChanged?: boolean;
  setExamFeedbackDate?: VoidFunction;
}) {
  if (props.examReturnDate != null) {
    return (
      <RequirementRow
        title="Return date"
        text={`Grades and feedback will be returned to students on ${formatFullSentence(
          new Date(props.examReturnDate)
        )}`}
        hasChanged={props.hasChanged}
      />
    );
  }
  return (
    <ActionableRequirementRow
      title={"Return date"}
      text="Select date"
      hasChanged={props.hasChanged}
      onAction={props.setExamFeedbackDate}
    />
  );
}

/** Display the writing time in minutes. */
export function WritingTime(props: {
  writingTime: number | null;
  errorMessage?: ReactNode;
  hasChanged?: boolean;
  isWindowExam: boolean;
  kind?: TaskKind;
}) {
  const { isWindowExam, errorMessage, writingTime, hasChanged, kind } = props;
  const isWindowExamPostfix = isWindowExam ? "in one sitting" : "";
  const text =
    errorMessage ??
    (writingTime != null
      ? `${writingTime} minute${
          writingTime === 1 ? "" : "s"
        } ${isWindowExamPostfix}`
      : "");
  const title = kind === TaskKind.Viva ? "Total response time" : "Writing time";
  return (
    <RequirementRow
      title={title}
      text={text}
      hasError={errorMessage != null}
      hasChanged={hasChanged}
    />
  );
}

/** Display the reading time in minutes. */
export function ReadingTime(props: {
  readingTime: number | null;
  errorMessage?: ReactNode;
  hasChanged?: boolean;
}) {
  const { errorMessage, readingTime, hasChanged } = props;
  const text =
    errorMessage ??
    (readingTime != null
      ? `${readingTime} minute${readingTime === 1 ? "" : "s"}`
      : "");

  return (
    <RequirementRow
      title="Reading time"
      text={text}
      hasError={errorMessage != null}
      hasChanged={hasChanged}
    />
  );
}

/** Display the exam start date and time. Display the title slightly differently based on the type of exam. */
export function ExamStart(props: {
  examType: ExamTiming;
  examStart: string | null;
  errorMessage?: ReactNode;
  hasChanged?: boolean;
  setExamStart?: VoidFunction;
  assessmentNoun?: AssessmentNoun;
}) {
  const formattedDate =
    props.examStart != null && formatFullSentence(new Date(props.examStart));
  const text = props.errorMessage ?? formattedDate ?? "";
  const isLive = props.examType === ExamTiming.Live;
  const noun = props.assessmentNoun ?? "Exam";
  const title = isLive ? `${noun} start` : `${noun} open`;

  if (props.examStart != null || props.errorMessage != null) {
    return (
      <RequirementRow
        title={title}
        text={text}
        hasError={props.errorMessage != null}
        hasChanged={props.hasChanged}
      />
    );
  }
  return (
    <ActionableRequirementRow
      title={title}
      text="Select date"
      hasChanged={props.hasChanged}
      onAction={props.setExamStart}
    />
  );
}

/** Display the exam end date and time. Display the title slightly differently based on the type of exam. */
export function ExamEnd(props: {
  examType: ExamTiming;
  examEnd: string | null;
  errorMessage?: ReactNode;
  hasChanged?: boolean;
  setExamEnd?: VoidFunction;
  assessmentNoun?: AssessmentNoun;
}) {
  const formattedDate =
    props.examEnd != null && formatFullSentence(new Date(props.examEnd));
  const text = props.errorMessage ?? formattedDate ?? "";
  const isLive = props.examType === ExamTiming.Live;
  const noun = props.assessmentNoun ?? "Exam";
  const title = isLive ? `${noun} end` : `${noun} close`;
  if (!(props.examEnd != null || props.errorMessage != null))
    return (
      <ActionableRequirementRow
        title={title}
        text="Select date"
        hasChanged={props.hasChanged}
        onAction={props.setExamEnd}
      />
    );

  return (
    <RequirementRow
      title={title}
      text={text}
      hasError={props.errorMessage != null}
      hasChanged={props.hasChanged}
    />
  );
}

/** Display that students will get a PDF copy of their submission. */
export function PdfSubmission(props: {
  isPdfCopyOn?: boolean;
  hasChanged?: boolean;
}) {
  return (
    <RequirementRow
      title="PDF copy"
      text="Students will receive a copy of their written submission"
      hasChanged={props.hasChanged}
    />
  );
}

/**
 * Display auto submission information, including the time up to
 * which students are allowed to submit late if autosubmission is off.
 **/
export function ExamAutosubmission(props: {
  /** If autosubmission is enabled then the late submission time limit is empty (or should be ignored at least) */
  isAutosubmisisonEnabled?: boolean;
  /** How late students can submit the exam (in minutes) in case auto submission is off. */
  lateSubmissionMinsTimeLimit: number | null;
  hasChanged?: boolean;
  assessmentNoun?: AssessmentNoun;
}) {
  const noun = (props.assessmentNoun ?? "Exam").toLowerCase();
  return (
    <RequirementRow
      title={
        props.isAutosubmisisonEnabled === true
          ? "Auto submission"
          : "No auto submission"
      }
      text={
        props.isAutosubmisisonEnabled === true
          ? `Student's work will be auto submitted at the end of the ${noun}`
          : props.lateSubmissionMinsTimeLimit != null
            ? `Students can submit late up to ${
                props.lateSubmissionMinsTimeLimit
              } minute${
                props.lateSubmissionMinsTimeLimit > 1 ? "s" : ""
              } after the ${noun} ends. They will not be able to submit after that`
            : `Students can submit late indefinitely after the ${noun} ends`
      }
      hasChanged={props.hasChanged}
    />
  );
}

export function ExamLDB(props: {
  ldbEnabled?: boolean;
  hasChanged?: boolean;
  assessmentNoun?: AssessmentNoun;
}) {
  const noun = (props.assessmentNoun ?? "Exam").toLowerCase();
  return (
    <RequirementRow
      title="LockDown Browser"
      text={
        props.ldbEnabled === true
          ? `Students must use LockDown Browser to complete the ${noun}`
          : "Students will not be required to use LockDown Browser"
      }
      hasChanged={props.hasChanged}
    />
  );
}

export function ExamSchoolyear(props: {
  examSchoolyearEnabled?: boolean;
  hasChanged?: boolean;
  assessmentNoun?: AssessmentNoun;
}) {
  const noun = (props.assessmentNoun ?? "Exam").toLowerCase();
  return (
    <RequirementRow
      title="Schoolyear"
      text={
        props.examSchoolyearEnabled === true
          ? `Students must use Schoolyear to complete the ${noun}`
          : "Students will not be required to use Schoolyear"
      }
      hasChanged={props.hasChanged}
    />
  );
}

export function ExamIpFilter(props: {
  examIpFilter: string | null;
  hasChanged?: boolean;
  assessmentNoun?: AssessmentNoun;
}) {
  const noun = (props.assessmentNoun ?? "Exam").toLowerCase();
  return (
    <RequirementRow
      title="IP address restriction"
      text={`Students can only work on the ${noun} from ${props.examIpFilter}`}
      hasChanged={props.hasChanged}
    />
  );
}

export function ExamForfeitRemainingTime(props: {
  submitAttemptLimit: number | null;
  hasChanged?: boolean;
}) {
  return (
    <RequirementRow
      title="Forfeit remaining time on submission"
      text={props.submitAttemptLimit == null ? "Disabled" : "Enabled"}
      hasChanged={props.hasChanged}
    />
  );
}
